import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useCarousel } from "../../hooks/useCarousel";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import kitchenImage from "../../assets/images/room/KITCHEN.jpg";
import livingRoomImage from "../../assets/images/room/LIVING-ROOM.jpg";
import bedroomImage from "../../assets/images/room/BEDROOM.jpg";
import wardrobeImage from "../../assets/images/room/WARDROBE.jpg";
import diningImage from "../../assets/images/room/DINING.jpg";
import kidsRoomImage from "../../assets/images/room/KIDS-ROOM.jpg";
import studyImage from "../../assets/images/room/STUDY.jpg";
import poojaImage from "../../assets/images/room/POOJA.jpg";
import spaceSavingImage from "../../assets/images/room/SPACE-SAVING.jpeg";
import falseCeilingImage from "../../assets/images/room/FALSE-CEILING.jpeg";
import doorsImage from "../../assets/images/room/DOORS.jpeg";
import lightingImage from "../../assets/images/room/LIGHTING.jpeg";

const rooms = [
  {
    name: "Kitchen",
    slug: "kitchen",
    image: kitchenImage,
    description: "Where you cook, we make it work beautifully.",
    features: [
      "Waterproof materials",
      "Easy-to-clean surfaces",
      "Layouts that fit your cooking style",
    ],
  },
  {
    name: "Living Room",
    slug: "living",
    image: livingRoomImage,
    description: "The heart of your home, designed for how you live.",
    features: [
      "TV units that hide clutter",
      "Storage that looks good",
      "Spaces for family time",
    ],
  },
  {
    name: "Bedroom",
    slug: "bedroom",
    image: bedroomImage,
    description: "Your personal space, designed for rest and organization.",
    features: [
      "Wardrobes that fit everything",
      "Study corners when you need them",
      "Lighting that helps you relax",
    ],
  },
  {
    name: "Wardrobe",
    slug: "wardrobe",
    image: wardrobeImage,
    description:
      "Finally, a place for everything. Organized, accessible, beautiful.",
    features: [
      "Walk-in options if you have space",
      "Smart organization systems",
      "Custom solutions for your needs",
    ],
  },
  {
    name: "Dining",
    slug: "dining",
    image: diningImage,
    description: "Where meals become memories. Make it special.",
    features: [
      "Storage for your crockery",
      "Bar counters if you entertain",
      "Designs that welcome guests",
    ],
  },
  {
    name: "Kids Room",
    slug: "kids",
    image: kidsRoomImage,
    description: "Spaces that grow with your child, safe and fun.",
    features: [
      "Study zones for homework",
      "Bunk beds to save space",
      "Safe, durable materials",
    ],
  },
  {
    name: "Study",
    slug: "study",
    image: studyImage,
    description: "A space where you can focus and get things done.",
    features: [
      "Desks that work for you",
      "Storage for books and files",
      "Cable management that keeps it tidy",
    ],
  },
  {
    name: "Pooja Room",
    slug: "pooja",
    image: poojaImage,
    description: "A sacred space crafted with care and respect.",
    features: [
      "Traditional designs that feel right",
      "Custom mandirs",
      "Storage for your needs",
    ],
  },
  {
    name: "Space Saving Furniture",
    slug: "space-saving",
    image: spaceSavingImage,
    description: "Smart furniture that maximizes every inch of your space.",
    features: [
      "Multi-functional designs",
      "Hidden storage solutions",
      "Compact living solutions",
    ],
  },
  {
    name: "False Ceiling",
    slug: "false-ceiling",
    image: falseCeilingImage,
    description:
      "Beautiful ceilings that add depth and character to your spaces.",
    features: [
      "Concealed lighting designs",
      "Modern patterns and finishes",
      "Architectural elegance",
    ],
  },
  {
    name: "Doors",
    slug: "doors",
    image: doorsImage,
    description: "Doors that welcome you home and complement your style.",
    features: [
      "Custom designs for your home",
      "Premium materials and finishes",
      "Perfect fit and function",
    ],
  },
  {
    name: "Lighting",
    slug: "lighting",
    image: lightingImage,
    description: "Lighting that sets the perfect mood in every space.",
    features: [
      "Ambient and task lighting",
      "Concealed LED solutions",
      "Smart lighting controls",
    ],
  },
];

export default function ShopByRoom() {
  const { emblaRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useCarousel({ align: "start", containScroll: "trimSnaps" });

  return (
    <section className="section-spacing bg-white">
      <div className="container-custom section-padding">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <SectionHeading
            label="Every Room Matters"
            title="Solutions for every space in your home."
            align="left"
            className="mb-0"
          />
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="w-12 h-12 rounded-full border-2 border-border-light flex items-center justify-center text-brand-primary hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden lg:grid grid-cols-4 gap-6">
          {rooms.map((room, index) => (
            <AnimatedSection key={room.slug} delay={index * 50}>
              <RoomCard room={room} />
            </AnimatedSection>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="lg:hidden overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {rooms.map((room) => (
              <div key={room.slug} className="flex-none w-[85%] sm:w-[45%]">
                <RoomCard room={room} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RoomCard({ room }) {
  return (
    <Link to={`/rooms/${room.slug}`} className="group block h-full">
      <div className="relative h-80 rounded-2xl overflow-hidden mb-4">
        <img
          src={room.image}
          alt={room.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Hover Content */}
        <div className="absolute inset-0 p-6 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
          <ul className="space-y-1 text-white text-sm mb-4">
            {room.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <span className="w-1 h-1 bg-brand-accent rounded-full" />
                {feature}
              </li>
            ))}
          </ul>
          <span className="inline-flex items-center gap-2 text-white font-medium">
            Explore {room.name}
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
      <h3 className="font-heading text-xl text-brand-primary group-hover:text-brand-accent transition-colors">
        {room.name}
      </h3>
      <p className="text-text-secondary text-sm mt-1">{room.description}</p>
    </Link>
  );
}
